import { Accordion, Banner, Bento, Carousel, Form, Hero, Split } from "@2u-landing-pages/templates";
import {
  isTypeBanner,
  TypeAccordionWithoutUnresolvableLinksResponse,
  TypeBannerWithoutUnresolvableLinksResponse,
  TypeBentoWithoutUnresolvableLinksResponse,
  TypeCarouselWithoutUnresolvableLinksResponse,
  TypeFormWithoutUnresolvableLinksResponse,
  TypeGalleryWithoutUnresolvableLinksResponse,
  TypeHeroWithoutUnresolvableLinksResponse,
  TypeProgram_variationWithoutUnresolvableLinksResponse,
  TypeSchoolWithoutUnresolvableLinksResponse,
  TypeSplitWithoutUnresolvableLinksResponse,
  TypeSticky_elementWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useIntersectionObserver } from "@2u-landing-pages/utils";
import EventClient from "@2u-landing-pages/utils/event-client";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import React, { useCallback } from "react";

type Section =
  | TypeHeroWithoutUnresolvableLinksResponse
  | TypeBannerWithoutUnresolvableLinksResponse
  | TypeAccordionWithoutUnresolvableLinksResponse
  | TypeBentoWithoutUnresolvableLinksResponse
  | TypeCarouselWithoutUnresolvableLinksResponse
  | TypeFormWithoutUnresolvableLinksResponse
  | TypeSplitWithoutUnresolvableLinksResponse
  | TypeGalleryWithoutUnresolvableLinksResponse
  | TypeSticky_elementWithoutUnresolvableLinksResponse;

interface ComponentRendererProps {
  section: Section;
  school: TypeSchoolWithoutUnresolvableLinksResponse;
  sectionName: string;
  phoneNumber: string;
  tuitionCost: string;
  programLogo: TypeProgram_variationWithoutUnresolvableLinksResponse["fields"]["logo_primary"];
  inspector: (props: { entryId: string; fieldId: string }) => any;
  isLoading: boolean;
}

const ComponentRenderer: React.FC<ComponentRendererProps> = ({ section, ...rest }) => {
  const id = section?.sys?.id;

  switch (section?.sys?.contentType?.sys?.id) {
    case "hero":
      return <Hero id={id} {...(section as TypeHeroWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "banner":
      return <Banner id={id} {...(section as TypeBannerWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "accordion":
      return <Accordion id={id} {...(section as TypeAccordionWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "bento":
      return <Bento id={id} {...(section as TypeBentoWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "carousel":
      return <Carousel id={id} {...(section as TypeCarouselWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "form":
      return <Form id={id} {...(section as TypeFormWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "split":
      return <Split id={id} {...(section as TypeSplitWithoutUnresolvableLinksResponse).fields} {...rest} />;
    default:
      return null;
  }
};

interface SectionsProps {
  sections: TypeProgram_variationWithoutUnresolvableLinksResponse["fields"]["sections"];
  school: TypeSchoolWithoutUnresolvableLinksResponse;
  programLogo: TypeProgram_variationWithoutUnresolvableLinksResponse["fields"]["logo_primary"];
  phoneNumber: string;
  tuitionCost: string;
  isLoading: boolean;
}

export const Sections: React.FC<SectionsProps> = ({
  phoneNumber,
  tuitionCost,
  sections,
  school,
  programLogo,
  isLoading,
}) => {
  const inspector = useContentfulInspectorMode();

  const handleIntersect = useCallback((target: HTMLElement) => {
    const { position, name } = target.dataset;

    EventClient.elementViewed({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: `Section ${position}`,
        text: name,
      },
    });
  }, []);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });

  return (
    <>
      {sections?.map((section, index) => {
        if (!section) return null;

        if (!section?.sys?.contentType?.sys?.id) return null;

        const style = {} as React.CSSProperties;
        if (isTypeBanner(section) && section?.fields?.sticky) {
          style.position = "sticky";
          style.top = "0";
          style.zIndex = 100;
        }

        return (
          <div
            key={section.sys.id}
            id={section.sys.id}
            data-name={section.fields.name}
            data-position={index + 1}
            ref={(element) => {
              if (element) addObserver(element);
            }}
            style={style}
          >
            <ComponentRenderer
              section={section as Section}
              school={school}
              sectionName={section.fields.name}
              phoneNumber={phoneNumber}
              tuitionCost={tuitionCost}
              programLogo={programLogo}
              inspector={inspector}
              isLoading={isLoading}
            />
          </div>
        );
      })}
    </>
  );
};
